import { tr, usePartnerTranslation } from "../../lib/language";
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { parseApiError } from "../../lib/api";
import { formatPartnerDate as format } from "../../lib/language";
import {
  Search,
  Calendar as CalendarIcon,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  X,
  Inbox,
  Filter,
} from "lucide-react";
import { Calendar } from "../../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { cn } from "../../lib/utils";
import BookingDetailsModal from "../../components/portal/BookingDetailsModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PER_PAGE = 15;

const STATUS_STYLES = {
  confirmed: "bg-[#D4F7DF] text-[#0F6B2B]",
  canceled_by_customer: "bg-[#FFE3A8] text-[#7A4E00]",
  canceled_by_supplier: "bg-[#FFBFBF] text-[#9B0000]",
  full_refund: "bg-[#D4CDFB] text-[#3326A3]",
  partial_refund: "bg-[#BDEFF4] text-[#0B5F6B]",
};

const StatusPill = ({
  status,
  label
}) => {
  usePartnerTranslation();
  return <span data-testid={`status-${status}`} className={cn("inline-flex items-center justify-center whitespace-nowrap rounded-full px-3 py-1 text-[12px] font-semibold", STATUS_STYLES[status] || "bg-[#F3EEEE] text-[#6B7280]")}>
    {label}
  </span>;
};

const formatActivityDate = (iso) => {
  if (!iso) return "—";
  try {
    return format(new Date(`${iso}T00:00:00`), "d-MMMM-yyyy");
  } catch {
    return iso;
  }
};

const formatPrice = (n, cur = "$") => {
  const v = Number(n) || 0;
  return `${cur} ${v.toFixed(2)}`;
};

/* ------------------ Filter Bar ------------------ */
const DateRangeField = ({ range, onChange }) => {
  usePartnerTranslation();
  const [open, setOpen] = useState(false);
  const label = (() => {
    if (!range?.from && !range?.to) return tr("From — to");
    if (range?.from && !range?.to) return `${format(range.from, "d MMM yyyy")} — …`;
    if (range?.from && range?.to)
      return `${format(range.from, "d MMM")} — ${format(range.to, "d MMM yyyy")}`;
    return tr("From — to");
  })();

  const clear = (e) => {
    e.stopPropagation();
    onChange(undefined);
  };

  const hasValue = !!(range?.from || range?.to);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="activity-date-range-trigger"
          className={cn(
            "inline-flex h-11 w-full items-center justify-between gap-2 rounded-lg border bg-white px-3 text-sm text-[#0F1115] outline-none transition-all hover:border-[#0F1115] focus-visible:ring-2 focus-visible:ring-[#FF5757]/30",
            hasValue ? "border-[#0F1115]" : "border-[#D7DAE0] text-[#8a8f99]",
          )}
        >
          <span className="inline-flex items-center gap-2 truncate">
            <CalendarIcon className="h-4 w-4 text-[#6B7280]" strokeWidth={1.8} />
            <span className="truncate">{label}</span>
          </span>
          {hasValue ? (
            <span
              onClick={clear}
              role="button"
              tabIndex={-1}
              aria-label={tr("Clear date range")}
              data-testid="activity-date-range-clear"
              className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[#6B7280] hover:bg-[#F3EEEE]"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          ) : (
            <ChevronDown className="h-4 w-4 text-[#6B7280]" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto rounded-xl border border-[#EAECEF] p-0 shadow-xl"
        data-testid="activity-date-range-popover"
      >
        <Calendar
          mode="range"
          numberOfMonths={2}
          selected={range}
          onSelect={onChange}
          defaultMonth={range?.from || new Date(2026, 9, 1)}
          className="p-3"
        />
        <div className="flex items-center justify-between border-t border-[#EAECEF] px-4 py-3">
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="text-sm font-medium text-[#6B7280] hover:text-[#0F1115]"
            data-testid="date-range-reset"
          >{tr("Reset")}</button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="humrahe-btn-primary inline-flex h-9 items-center rounded-md px-4 text-sm font-semibold"
            data-testid="date-range-apply"
          >{tr("Apply")}</button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

/* ------------------ Sort menu ------------------ */
const SORT_OPTIONS = [
  { value: "activity_date-desc", get label() { return tr("Activity date: newest first"); }, sort_by: "activity_date", sort_dir: "desc" },
  { value: "activity_date-asc", get label() { return tr("Activity date: oldest first"); }, sort_by: "activity_date", sort_dir: "asc" },
  { value: "status-asc", get label() { return tr("Status: A → Z"); }, sort_by: "status", sort_dir: "asc" },
  { value: "status-desc", get label() { return tr("Status: Z → A"); }, sort_by: "status", sort_dir: "desc" },
];

const SortMenu = ({ value, onChange }) => {
  usePartnerTranslation();
  const [open, setOpen] = useState(false);
  const current = SORT_OPTIONS.find((o) => o.value === value) || SORT_OPTIONS[0];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="sort-trigger"
          className="inline-flex h-11 items-center gap-2 rounded-lg border border-[#D7DAE0] bg-white px-3 text-sm font-medium text-[#0F1115] outline-none transition-colors hover:border-[#0F1115]"
        >
          <ArrowUpDown className="h-4 w-4 text-[#6B7280]" strokeWidth={1.8} />
          <span className="hidden sm:inline">{current.label}</span>
          <span className="sm:hidden">{tr("Sort")}</span>
          <ChevronDown className="h-4 w-4 text-[#6B7280]" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-64 rounded-xl border border-[#EAECEF] p-1 shadow-lg"
      >
        {SORT_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            data-testid={`sort-option-${opt.value}`}
            onClick={() => {
              onChange(opt.value);
              setOpen(false);
            }}
            className={cn(
              "flex w-full items-center justify-between rounded-md px-3 py-2.5 text-start text-sm font-medium transition-colors",
              opt.value === value
                ? "bg-[#FFF2F2] text-[#FF5757]"
                : "text-[#0F1115] hover:bg-[#F7F7F9]",
            )}
          >
            <span>{opt.label}</span>
            {opt.value === value && (
              <span className="ms-3 inline-block h-2 w-2 rounded-full bg-[#FF5757]" />
            )}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};

/* ------------------ Pagination ------------------ */
const Pager = ({ page, totalPages, onChange }) => {
  usePartnerTranslation();
  const pages = useMemo(() => {
    if (totalPages <= 1) return [];
    // Compact window: show up to 5 numbered pages centered around `page`
    const max = 5;
    if (totalPages <= max) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const start = Math.min(
      Math.max(1, page - Math.floor(max / 2)),
      totalPages - max + 1,
    );
    return Array.from({ length: max }, (_, i) => start + i);
  }, [page, totalPages]);

  if (totalPages <= 1) return null;

  const btn =
    "inline-flex h-9 w-9 items-center justify-center rounded-md text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40";

  return (
    <nav
      aria-label={tr("Pagination")}
      data-testid="bookings-pagination"
      className="mt-8 flex items-center justify-center gap-1 text-[#0F1115]"
    >
      <button
        type="button"
        data-testid="page-first"
        onClick={() => onChange(1)}
        disabled={page === 1}
        aria-label={tr("First page")}
        className={cn(btn, "hover:bg-[#F3EEEE]")}
      >
        <ChevronsLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        data-testid="page-prev"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
        aria-label={tr("Previous page")}
        className={cn(btn, "hover:bg-[#F3EEEE]")}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      {pages[0] > 1 && (
        <span className="px-1 text-sm text-[#9BA1AB]">…</span>
      )}
      {pages.map((p) => {
        const active = p === page;
        return (
          <button
            key={p}
            type="button"
            data-testid={`page-${p}`}
            onClick={() => onChange(p)}
            aria-current={active ? "page" : undefined}
            className={cn(
              btn,
              active
                ? "text-[#FF5757]"
                : "text-[#0F1115] hover:bg-[#F3EEEE]",
            )}
          >
            <span className={active ? "font-extrabold underline underline-offset-4 decoration-2" : ""}>
              {p}
            </span>
          </button>
        );
      })}
      {pages[pages.length - 1] < totalPages && (
        <span className="px-1 text-sm text-[#9BA1AB]">…</span>
      )}
      <button
        type="button"
        data-testid="page-next"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        aria-label={tr("Next page")}
        className={cn(btn, "hover:bg-[#F3EEEE]")}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
      <button
        type="button"
        data-testid="page-last"
        onClick={() => onChange(totalPages)}
        disabled={page === totalPages}
        aria-label={tr("Last page")}
        className={cn(btn, "hover:bg-[#F3EEEE]")}
      >
        <ChevronsRight className="h-4 w-4" />
      </button>
    </nav>
  );
};

/* ------------------ Table row ------------------ */
const BookingRow = ({
  b,
  onView
}) => {
  usePartnerTranslation();
  return <tr data-testid={`booking-row-${b.reference}`} className="group cursor-pointer border-b border-[#F0EFEF] transition-colors hover:bg-[#FFF7F7]" onClick={() => onView(b)}>
    <td className="whitespace-nowrap px-3 py-4 text-sm font-semibold text-[#0F1115] sm:px-4">
      {b.reference}
    </td>
    <td className="px-3 py-4 text-sm text-[#0F1115] sm:px-4">
      <span className="line-clamp-1">{b.product_title}</span>
    </td>
    <td className="whitespace-nowrap px-3 py-4 text-sm text-[#0F1115] sm:px-4">
      {b.option_code}
    </td>
    <td className="whitespace-nowrap px-3 py-4 text-sm text-[#0F1115] sm:px-4">
      {formatActivityDate(b.activity_date)}
    </td>
    <td className="whitespace-nowrap px-3 py-4 text-sm text-[#0F1115] sm:px-4">
      {b.activity_time}
    </td>
    <td className="whitespace-nowrap px-3 py-4 text-sm font-semibold text-[#0F1115] sm:px-4">
      {formatPrice(b.retail_price, b.currency)}
    </td>
    <td className="whitespace-nowrap px-3 py-4 sm:px-4">
      <StatusPill status={b.status} label={b.status_label} />
    </td>
    <td className="whitespace-nowrap px-3 py-4 text-end sm:px-4">
      <button type="button" data-testid={`view-details-${b.reference}`} onClick={e => {
        e.stopPropagation();
        onView(b);
      }} className="text-sm font-semibold text-[#2476FF] underline-offset-4 hover:underline">{tr("View Details")}</button>
    </td>
  </tr>;
};

/* ------------------ Mobile card ------------------ */
const BookingCard = ({
  b,
  onView
}) => {
  usePartnerTranslation();
  return <article data-testid={`booking-card-${b.reference}`} onClick={() => onView(b)} className="cursor-pointer rounded-xl border border-[#EAECEF] bg-white p-4 transition-colors hover:border-[#FF5757] hover:bg-[#FFF7F7]">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs font-medium text-[#6B7280]">{tr("Ref")}</p>
        <p className="text-sm font-bold tracking-tight text-[#0F1115]">
          {b.reference}
        </p>
      </div>
      <StatusPill status={b.status} label={b.status_label} />
    </div>
    <p className="mt-3 line-clamp-2 text-sm font-semibold text-[#0F1115]">
      {b.product_title}
    </p>
    <p className="mt-1 text-xs text-[#6B7280]">{tr("Option ")}{b.option_code}</p>
    <div className="mt-3 flex items-center justify-between text-xs text-[#0F1115]">
      <span>
        {formatActivityDate(b.activity_date)} · {b.activity_time}
      </span>
      <span className="text-sm font-bold">
        {formatPrice(b.retail_price, b.currency)}
      </span>
    </div>
    <div className="mt-3 text-end">
      <span className="text-sm font-semibold text-[#2476FF]">{tr("View Details →")}</span>
    </div>
  </article>;
};

/* ------------------ Empty state ------------------ */
const EmptyState = ({
  hasFilters,
  onClear
}) => {
  usePartnerTranslation();
  return <div data-testid="bookings-empty" className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#EAECEF] bg-[#FAFAFB] py-16 text-center">
    <Inbox className="h-14 w-14 text-[#CFD2D8]" strokeWidth={1.3} />
    <p className="mt-4 text-lg font-semibold text-[#0F1115]">
      {hasFilters ? tr("No bookings match your filters") : tr("No bookings yet")}
    </p>
    <p className="mt-1 max-w-md text-sm text-[#6B7280]">
      {hasFilters ? tr("Try adjusting the booking reference or date range to see more results.") : tr("When a customer books one of your products, their booking will appear here.")}
    </p>
    {hasFilters && <button type="button" data-testid="bookings-empty-clear" onClick={onClear} className="mt-5 inline-flex h-10 items-center gap-2 rounded-lg border border-[#0F1115] px-4 text-sm font-semibold text-[#0F1115] transition-colors hover:bg-[#0F1115] hover:text-white">
        <X className="h-4 w-4" />{tr("Clear filters")}</button>}
  </div>;
};

/* ------------------ Page ------------------ */
export default function BookingsPage() {
  usePartnerTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [reference, setReference] = useState("");
  const [dateRange, setDateRange] = useState(undefined);
  const [sort, setSort] = useState("activity_date-desc");
  const [activeBooking, setActiveBooking] = useState(null);
  const debounceRef = useRef(null);
  const [debouncedRef, setDebouncedRef] = useState("");

  // Debounce the reference input so we don't fire a request per keystroke
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedRef(reference.trim());
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [reference]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedRef, dateRange, sort]);

  useEffect(() => {
    let cancelled = false;
    const opt = SORT_OPTIONS.find((o) => o.value === sort) || SORT_OPTIONS[0];
    const params = {
      page,
      per_page: PER_PAGE,
      sort_by: opt.sort_by,
      sort_dir: opt.sort_dir,
    };
    if (debouncedRef) params.reference = debouncedRef;
    if (dateRange?.from)
      params.date_from = format(dateRange.from, "yyyy-MM-dd");
    if (dateRange?.to) params.date_to = format(dateRange.to, "yyyy-MM-dd");

    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/bookings`, { params });
        if (cancelled) return;
        setItems(res.data.items || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.total_pages || 0);
      } catch (err) {
        const { message } = parseApiError(err, tr("Couldn't load bookings."));
        if (!cancelled) {
          setItems([]);
          setTotal(0);
          setTotalPages(0);
          if (process.env.NODE_ENV !== "production") {
            console.warn("[bookings] load failed:", message);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [page, debouncedRef, dateRange, sort]);

  const hasFilters = !!(debouncedRef || dateRange?.from || dateRange?.to);

  const clearFilters = () => {
    setReference("");
    setDateRange(undefined);
  };

  return (
    <main
      data-testid="bookings-page"
      className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-10 sm:px-6 md:px-10"
    >
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-[#0F1115] md:text-4xl">{tr("Bookings")}</h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            {loading
              ? tr("Loading bookings…")
              : total === 0
                ? tr("No bookings to show")
                : tr("Bookings: {{count}}", { count: total })}
          </p>
        </div>
      </header>

      {/* Filters */}
      <section
        data-testid="bookings-filters"
        className="mt-6 flex flex-col gap-4 md:flex-row md:items-end md:gap-4"
      >
        <div className="flex-1">
          <label
            htmlFor="booking-reference"
            className="block text-sm font-medium text-[#0F1115]"
          >{tr("Booking reference")}</label>
          <div className="relative mt-2">
            <Search
              className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9BA1AB]"
              strokeWidth={1.8}
            />
            <input
              id="booking-reference"
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="HML123ABCAS12"
              data-testid="booking-reference-input"
              className="h-11 w-full rounded-lg border border-[#D7DAE0] bg-white ps-10 pe-10 text-sm text-[#0F1115] outline-none transition-all placeholder:text-[#B6BAC2] focus:border-[#FF5757] focus:shadow-[0_0_0_4px_rgba(255,87,87,0.1)]"
            />
            {reference && (
              <button
                type="button"
                onClick={() => setReference("")}
                aria-label={tr("Clear search")}
                data-testid="booking-reference-clear"
                className="absolute end-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-[#6B7280] hover:bg-[#F3EEEE]"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium text-[#0F1115]">{tr("Activity date")}</label>
          <div className="mt-2">
            <DateRangeField range={dateRange} onChange={setDateRange} />
          </div>
        </div>

        <div className="flex-shrink-0">
          <label className="sr-only">{tr("Sort")}</label>
          <SortMenu value={sort} onChange={setSort} />
        </div>

        {hasFilters && (
          <button
            type="button"
            onClick={clearFilters}
            data-testid="clear-all-filters"
            className="inline-flex h-11 flex-shrink-0 items-center gap-2 rounded-lg border border-[#0F1115] px-4 text-sm font-semibold text-[#0F1115] transition-colors hover:bg-[#0F1115] hover:text-white"
          >
            <X className="h-4 w-4" />{tr("Clear")}</button>
        )}
      </section>

      {/* Results */}
      <section className="mt-8">
        {loading && items.length === 0 ? (
          <div
            data-testid="bookings-loading"
            className="flex items-center justify-center py-24"
          >
            <Loader2 className="h-6 w-6 animate-spin text-[#FF5757]" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState hasFilters={hasFilters} onClear={clearFilters} />
        ) : (
          <>
            {/* Desktop table */}
            <div
              data-testid="bookings-table-wrapper"
              className="hidden overflow-x-auto rounded-xl border border-[#EAECEF] bg-white md:block"
            >
              <table className="w-full min-w-[900px] border-collapse text-start">
                <thead>
                  <tr className="border-b border-[#EAECEF] bg-[#FAFAFB] text-[12px] font-semibold uppercase tracking-wider text-[#6B7280]">
                    <th scope="col" className="px-4 py-3">{tr("Reference")}</th>
                    <th scope="col" className="px-4 py-3">{tr("Product")}</th>
                    <th scope="col" className="px-4 py-3">{tr("Option")}</th>
                    <SortableHeader
                      label={tr("Date")}
                      field="activity_date"
                      sort={sort}
                      onChange={setSort}
                    />
                    <th scope="col" className="px-4 py-3">{tr("Time")}</th>
                    <th scope="col" className="px-4 py-3">{tr("Retail Price")}</th>
                    <SortableHeader
                      label={tr("Status")}
                      field="status"
                      sort={sort}
                      onChange={setSort}
                    />
                    <th scope="col" className="px-4 py-3 text-end">{tr("Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((b) => (
                    <BookingRow
                      key={b.id}
                      b={b}
                      onView={setActiveBooking}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div
              data-testid="bookings-cards"
              className="grid grid-cols-1 gap-3 md:hidden"
            >
              {items.map((b) => (
                <BookingCard
                  key={b.id}
                  b={b}
                  onView={setActiveBooking}
                />
              ))}
            </div>

            <Pager
              page={page}
              totalPages={totalPages}
              onChange={setPage}
            />
          </>
        )}
      </section>

      <BookingDetailsModal
        booking={activeBooking}
        onClose={() => setActiveBooking(null)}
        onBookingUpdate={(updated) => {
          setItems((prev) =>
            prev.map((b) => (b.reference === updated.reference ? updated : b)),
          );
          setActiveBooking(updated);
        }}
      />
    </main>
  );
}

/* ------------------ Sortable column header ------------------ */
const SortableHeader = ({ label, field, sort, onChange }) => {
  usePartnerTranslation();
  const [sf, sd] = sort.split("-");
  const active = sf === field;
  const dir = active ? sd : null;
  const next = !active
    ? `${field}-desc`
    : dir === "desc"
      ? `${field}-asc`
      : `${field}-desc`;
  return (
    <th scope="col" className="px-4 py-3">
      <button
        type="button"
        data-testid={`sort-header-${field}`}
        onClick={() => onChange(next)}
        className={cn(
          "inline-flex items-center gap-1 text-[12px] font-semibold uppercase tracking-wider transition-colors",
          active ? "text-[#0F1115]" : "text-[#6B7280] hover:text-[#0F1115]",
        )}
      >
        <span>{label}</span>
        {dir === "asc" && <ChevronUp className="h-3.5 w-3.5" />}
        {dir === "desc" && <ChevronDown className="h-3.5 w-3.5" />}
        {!active && <ArrowUpDown className="h-3.5 w-3.5 opacity-60" />}
      </button>
    </th>
  );
};
